import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Spinner, Alert } from "react-bootstrap";
import { useConversations } from "../context/ConversationsContext";
import { useDashboards } from "../context/DashboardsContext";
import * as api from "../api/client";
import WidgetCard from "../components/WidgetCard";

/** Shorten to `max` characters on a word boundary, so a generated title never ends mid-word. Returns
 *  the text unchanged when it already fits, and falls back to a hard cut for a single long word. */
function truncateWords(text, max) {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > max * 0.5 ? cut.slice(0, lastSpace) : cut).replace(/[,\s]+$/, "") + "…";
}

/** Preview what a dashboard built from the currently-selected chats would contain, tick the tiles that
 * belong on it, then save. Widgets are fetched fresh here (not reused from ChatPage state) so this
 * works for chats you selected without ever opening them this session.
 *
 * The tiles rendered here ARE what gets saved — the specs below are posted straight to the server, so
 * the save is a single INSERT. There used to be a second, AI-curated button ("Build with AI curator")
 * that re-derived everything server-side and had a model choose the keepers; it was removed because
 * the checkboxes do that job directly, without its 30-90s of model time and without the server
 * repeating every query the preview had already run.
 */
export default function DashboardBuilderPage() {
  const navigate = useNavigate();
  const { conversations, selected, clearSelected } = useConversations();
  const { refresh: refreshDashboards } = useDashboards();

  const selectedChats = conversations.filter((c) => selected.has(c.id));
  const [loading, setLoading] = useState(true);
  const [widgetsByChat, setWidgetsByChat] = useState({}); // chatId -> widgets[]
  // Which previewed tiles become the dashboard, by widget id. Everything starts ticked: the common
  // case is "all of these, minus one or two", so opt-out is fewer clicks than opt-in.
  const [picked, setPicked] = useState(() => new Set());
  // Chart type chosen per tile in the card header, by widget id. Persisted with the save, unlike the
  // same control elsewhere in the app — here it is the user making the presentation call the curator
  // used to make (a date series reads better as a line than as bars).
  const [types, setTypes] = useState({});
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selected.size === 0) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const entries = await Promise.all(
        [...selected].map(async (id) => {
          try {
            const resp = await api.listConversationWidgets(id);
            return [id, resp.widgets || []];
          } catch {
            return [id, []];
          }
        })
      );
      if (cancelled) return;
      const byChat = Object.fromEntries(entries);
      setWidgetsByChat(byChat);
      setPicked(new Set(Object.values(byChat).flat().map((w) => w.id)));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selected]);

  // Suggest a name once widgets have loaded, built only from chats that actually contributed a
  // chart/table. Naming after every *selected* chat (the previous behavior) produced titles like
  // "tell me your name + count bookings by booking status" whenever an unrelated small-talk chat was
  // checked alongside a real one — misleading, since that chat contributed nothing to the dashboard.
  useEffect(() => {
    if (title || loading) return;
    const contributing = selectedChats.filter((c) => (widgetsByChat[c.id] || []).length > 0);
    if (contributing.length === 0) return;
    // Deduped: continuing one topic across several chats leaves them all named the same thing, and
    // joining those verbatim produced "Devco a. Last + Current FY- Sales & Collection, Devco a. Last +
    // Current FY- Sales & Collection, Devc" — the same sentence three times, then cut mid-word.
    const names = [...new Set(contributing.map((c) => (c.title || "Untitled chat").trim()))];
    const shown = names.slice(0, 2).join(", ");
    const extra = names.length - Math.min(names.length, 2);
    setTitle(truncateWords(shown, 70) + (extra > 0 ? ` +${extra} more` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, widgetsByChat]);

  const totalWidgets = Object.values(widgetsByChat).reduce((n, ws) => n + ws.length, 0);
  const pickedCount = picked.size;

  const toggle = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  /** Tick or untick every tile from one chat at once — the usual way a whole chat gets excluded. */
  const setChatPicked = (chatId, on) =>
    setPicked((prev) => {
      const next = new Set(prev);
      for (const w of widgetsByChat[chatId] || []) {
        if (on) next.add(w.id);
        else next.delete(w.id);
      }
      return next;
    });

  const setAllPicked = (on) =>
    setPicked(on ? new Set(Object.values(widgetsByChat).flat().map((w) => w.id)) : new Set());

  /** The ticked tiles, as the server should store them: the spec exactly as previewed (with any chart
   *  type chosen here baked in) plus the conversation it came from, which the server re-checks against
   *  the caller before saving. */
  const payload = useMemo(
    () =>
      Object.entries(widgetsByChat).flatMap(([chatId, widgets]) =>
        widgets
          .filter((w) => picked.has(w.id))
          .map((w) => ({
            kind: w.kind,
            spec: types[w.id] && w.kind === "chart" ? { ...w.spec, type: types[w.id] } : w.spec,
            conversationId: chatId,
          }))
      ),
    [widgetsByChat, picked, types]
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only the chats that actually contributed a ticked tile — passing every checked chat would
      // record a source the dashboard shows nothing from, and would re-add its widgets on a later
      // "add widgets from these chats" (which skips conversations already listed).
      const chatIds = [...new Set(payload.map((w) => w.conversationId))];
      const dashboard = await api.createDashboard(title.trim() || "Untitled dashboard", chatIds, payload);
      clearSelected();
      await refreshDashboards();
      navigate(`/dashboards/${dashboard.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (selected.size === 0) {
    return (
      <main className="main">
        <div className="topbar">
          <h1>Build a dashboard</h1>
        </div>
        <div className="page-body">
          <Alert variant="secondary">
            No chats selected. Check the box next to one or more chats in the sidebar, then click{" "}
            <strong>Build dashboard</strong>.
          </Alert>
        </div>
      </main>
    );
  }

  return (
    <main className="main">
      <div className="topbar">
        <h1>Build a dashboard</h1>
        <p>
          {selectedChats.length} chat{selectedChats.length !== 1 ? "s" : ""} selected · {pickedCount} of{" "}
          {totalWidgets} widget{totalWidgets !== 1 ? "s" : ""} included
        </p>
      </div>

      <div className="page-body">
        <Form.Group className="mb-3" style={{ maxWidth: 480 }}>
          <Form.Label>Dashboard name</Form.Label>
          <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Q3 sales review" />
        </Form.Group>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <Spinner animation="border" size="sm" />
        ) : totalWidgets === 0 ? (
          <Alert variant="warning">
            None of the selected chats produced a chart or table yet — ask for one (e.g. "as a bar chart" or
            "show N rows") before building a dashboard from them.
          </Alert>
        ) : (
          <>
            <div className="builder-toolbar">
              <span>Tick the tiles you want on the dashboard.</span>
              <Button variant="link" size="sm" onClick={() => setAllPicked(true)} disabled={saving}>
                Select all
              </Button>
              <Button variant="link" size="sm" onClick={() => setAllPicked(false)} disabled={saving}>
                Clear all
              </Button>
            </div>

            {selectedChats.map((c) => {
              const widgets = widgetsByChat[c.id] || [];
              if (widgets.length === 0) return null;
              const on = widgets.filter((w) => picked.has(w.id)).length;
              return (
                <div key={c.id} className="builder-group">
                  <div className="builder-group-head">
                    <div className="who">{c.title}</div>
                    <button
                      type="button"
                      className="builder-group-toggle"
                      onClick={() => setChatPicked(c.id, on < widgets.length)}
                      disabled={saving}
                    >
                      {on}/{widgets.length} · {on < widgets.length ? "select all" : "clear"}
                    </button>
                  </div>
                  <div className="widget-grid">
                    {widgets.map((w) => (
                      <WidgetCard
                        key={w.id}
                        kind={w.kind}
                        spec={w.spec}
                        selectable
                        selected={picked.has(w.id)}
                        onToggle={() => toggle(w.id)}
                        onChartTypeChange={(t) => setTypes((prev) => ({ ...prev, [w.id]: t }))}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="builder-actions">
              <Button variant="primary" onClick={save} disabled={saving || pickedCount === 0}>
                {saving ? "Saving…" : `Create dashboard with ${pickedCount} widget${pickedCount !== 1 ? "s" : ""}`}
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate(-1)} disabled={saving}>
                Cancel
              </Button>
            </div>
            {!saving && pickedCount === 0 && (
              <p className="builder-note">Tick at least one tile above to create a dashboard.</p>
            )}
          </>
        )}
      </div>
    </main>
  );
}
